import { defineComponent as _defineComponent } from 'vue'
import { defineMessages } from '@/lib/i18n';
import { i18n as i18nModule } from '@/plugins/vue-i18n';
import {
  CreateCompanySdsRequestUnit,
  DuplicatedSdsItem,
} from '@/interfaces/api/general/company';
import { Department } from '@/interfaces/model/general/department';
import { MAX_FILE_SIZE } from '@/lib/constant';
import { getPdfDisplayOptions } from '@/lib/formItem';
import { SYMBOL_REGEX, patternExtractFileExtension } from '@/lib/regex';
import router from '@/router';
import { store } from '@/store/index';
import {
  dispatchCheckCompanySdsesDuplicates,
  dispatchCreateCompanySdses,
  dispatchExportCompanySdsJsonToPdf,
  dispatchGetCompanySdsesCount,
  dispatchGetGcsSignedUrl,
} from '@/store/main/actionsMain';
import {
  readAssertedCompany,
  readAssertedCompanyId,
} from '@/store/main/getters';
import {
  commitAddNotification,
  commitSetCompany,
} from '@/store/main/mutations';
import BaseButtonOutlined from '@/views/base/BaseButtonOutlined.vue';
import BaseButtonSubmit from '@/views/base/BaseButtonSubmit.vue';
import PdfViewer from '@/views/dashboard/PdfViewer.vue';
import UploadFileTable from '@/views/pages/sdsUpload/UploadFileTable.vue';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { usePermissionComposable } from '@/composables/usePermission';
import { useAlertMessages } from '@/composables/useAlertMessages';
import { Tag } from '@/interfaces/model/general/tag';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import UUID from 'uuidjs';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import type { Values } from 'vue-i18n';
import PdfjsWorkerUrl from 'file-loader?esModule=false&name=[name].[contenthash].js!pdfjs-dist/legacy/build/pdf.worker.min.js';
import DepartmentTreeSelect from '@/components/DepartmentTreeSelect.vue';
import BaseBottomSheet from '@/views/base/BaseBottomSheet.vue';
import { useTagComposable } from '@/composables/useTag';
import TagChip from '@/components/TagChip.vue';
import { calculateFileHash } from '@/utils/hash-utils';
import {
  classifyPdfHeader,
  hasMicrosoftIrmPayload,
} from '@/utils/pdf-header-utils';

import sharedMessages from '@/lib/shared-messages';
import {
  updateSdsCount,
  updateSdsCountDoneSignal,
} from '@/signals/UpdateSdsCount';

export interface FileWithTag extends File {
  tags: Tag[];
}

interface UploadFile {
  lastModified: number;
  lastModifiedDate: unknown;
  name: string;
  size: number;
  type: string;
  webkitRelativePath: string;
}

type PdfProtection = 'password' | 'irm' | null;

type InspectedFile = { hash: string | null; message: string | null };


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsUploadPage',
  setup(__props) {

const { tags, getCompanyTag } = useTagComposable();

pdfjsLib.GlobalWorkerOptions.workerPort = new Worker(PdfjsWorkerUrl);

const $t = (key: string, values?: Values): string =>
  i18nModule.tc(key, 1, values);
const pdfDisplayOptions = getPdfDisplayOptions();

const m = defineMessages({
  sdsUploadNotice: 'SDS登録時の注意事項',
  fileFormatNotice:
    'ファイル形式はPDF(.pdf)です。Excel, Word, PNG, ZIP, docx等のファイル形式は正常に登録できません。',
  fileSizeNotice:
    'アップロードできるファイル容量は、1ファイルあたり最大10MBです。超過するとアップロードできません。',
  passwordProtectedNotice:
    'パスワードで保護されたPDFファイルは解析することができません。パスワード保護を解除した上でアップロードしてください。',
  multiplePdfNotice: 'PDFは複数件まとめて(上限50件まで)アップロードできます。',
  moreInfo: '詳細は{0}をご確認ください。',
  uploadPdf: 'PDFをアップロード',
  multiplePdfUploadNotice: 'PDFは複数件まとめてアップロードできます。',
  dragDropFileHere: 'ここにドラッグ&ドロップ',
  or: 'または',
  selectFileToUpload: 'ファイルを選択してアップロード',
  registerSds: '登録するSDS',
  confirmFilesToUpload: '下記ファイルをアップロードしてよろしいですか？',
  totalFiles: '全{count}件',
  departmentRestrictionNotice:
    '編集者権限ユーザーがSDSに登録できるのは、自分が所属している部署のみです。',
  departmentSelectionGuide:
    '登録したい部署が選択肢に表示されない場合は、マイページから所属部署を追加してください。',
  adminGuide:
    '※管理者権限ユーザーの場合は、すべての部署をSDSに登録することができます。',
  register: '登録',
  uploadingFiles: '{uploaded} / {total}件のファイルアップロード中',
  errorCheckFiles: '（エラー）ファイルをご確認ください',
  fileSizeLimitNotice:
    'アップロードできるファイル容量は、1ファイルあたり最大10MBです。',
  fileCountLimitNotice: 'アップロードできるファイル数の上限は50件までです。',
  uploadAccepted: 'アップロードを受け付けました',
  uploadFailed: 'アップロードに失敗しました。時間をおいて再度お試しください。',
  jsonPdfExportFailed:
    'PDFの生成に失敗しました。JSONファイルの内容をご確認ください。',
  subjectExceedsFileSizeLimit:
    '{subject} はファイル容量の上限（10MB）を超えています。10MB 以下のファイルを選んでください。',
  subjectIsNotPdf:
    '{subject} は PDF ファイルではありません。拡張子が .pdf のファイルを選んでください。',
  subjectIsPasswordProtected: '{subject} はパスワードで保護されています。',
  subjectIsEmpty:
    '{subject} は空のファイル（0バイト）です。ファイルの内容をご確認ください。',
  subjectIsNotReadablePdf:
    '{subject} はPDFとして読み込めません。ファイルが破損しているか、PDF形式ではありません。',
  subjectIsRmsProtected:
    '{subject} には社内の情報保護（IRM）がかかっています。保護を解除した PDF をアップロードしてください。',
  subjectReadFailed:
    '{subject} の読み込みに失敗しました。もう一度お試しください。',
  bulkTagEdit: '一括タグ編集',
  bulkTagEditTitle: '選択中のSDSに一括でタグをつける',
  filesSelectedAll: 'このページ内の{0}のすべてが選択されています。',
  filesSelectedSome: 'このページ内の{0}が選択されています。',
  selectAllFiles: '{0}すべて選択',
});

const {
  companyDepartments,
  getCompanyDepartments,
  departmentTree,
  getCompanyDepartmentTree,
  ownDepartments,
  editableDepartments,
  editableDepartmentTree,
} = useDepartmentComposable();
const { hasPermission, PERMISSIONS: P } = usePermissionComposable();

watch(updateSdsCountDoneSignal, () => router.push({ name: 'sdsList' }));

// ロケールに基づいてヘルプセンターURLを設定
const helpPageUrl = computed(() => {
  return i18nModule.locale === 'ja-JP'
    ? process.env.VUE_APP_SERVICE_HELP_DOCUMENT
    : process.env.VUE_APP_SERVICE_HELP_DOCUMENT_EN;
});
const selectedPdfDisplayOption = ref<string>('none');
const pdfBlob = ref<string>('');
const previewedSdsUuid = ref<string>('');
const files = ref<Array<FileWithTag>>([]);
const fileMetas = ref<Array<CreateCompanySdsRequestUnit>>([]);
const sdsUuids = computed(() => fileMetas.value.map((meta) => meta.sds_uuid));
const jsonInput = ref<null | HTMLInputElement>(null);
const isJsonExporting = ref<boolean>(false);

const uploadWithJson = (): void => {
  if (isJsonExporting.value) return;
  jsonInput.value?.click();
};

const selectJsonFile = async (e: Event): Promise<void> => {
  const target = e.target as HTMLInputElement;
  const jsonFile = target.files?.[0];
  target.value = '';
  if (!jsonFile) return;

  isJsonExporting.value = true;
  try {
    const formData = new FormData();
    formData.append('file', jsonFile);
    const response = await dispatchExportCompanySdsJsonToPdf(store, {
      companyId: readAssertedCompanyId(store),
      data: formData,
    });
    if (!response?.data) return;

    const contentType = String(response.headers['content-type'] ?? '');
    if (contentType.includes('application/json')) {
      // 本番構成: label exportがGCSへ置いた署名付きダウンロードURLが返る
      const text = await (response.data as Blob).text();
      const downloadUrl = JSON.parse(text).download_url;
      if (downloadUrl) {
        // await後のwindow.openはポップアップブロックされることがあるため、
        // ブロックされた場合は同一タブで開く（署名URLはattachmentなので画面遷移しない）
        const opened = window.open(downloadUrl, '_blank');
        if (!opened) {
          window.location.href = downloadUrl;
        }
      }
      return;
    }

    // ローカル構成（GCSなし）: PDFバイトが直接返る
    const url = window.URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${jsonFile.name.replace(
      patternExtractFileExtension,
      ''
    )}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
    commitAddNotification(store, {
      messageKey: m.jsonPdfExportFailed,
      color: 'error',
    });
  } finally {
    isJsonExporting.value = false;
  }
};
const input = ref<null | HTMLInputElement>(null);
const isUploading = ref<boolean>(false);
const uploaded = ref<number>(0);
const isCheckedAllFiles = ref<boolean>(false);
const checkedSdsUuids = ref<string[]>([]);
const isShowFilesTagOperationDialog = ref<boolean>(false);
const searchTagKeyword = ref<string>('');
const tempSelectedTags = ref<Tag[]>([]);

const pdfFile = reactive<UploadFile>({
  lastModified: 0,
  lastModifiedDate: {},
  size: 0,
  name: '',
  type: '',
  webkitRelativePath: '',
});

const uploadDialog = ref<boolean>(false);
const duplicates = ref<DuplicatedSdsItem[]>([]);
const duplicatesChecked = ref<boolean>(false);
const isCheckingDuplicates = ref<boolean>(false);
const { alertMessages, clearAlertMessages, pushAlertMessage } =
  useAlertMessages();
const isValidForm = ref<boolean>(false);
const selectableDepartments = computed(() => {
  return hasPermission(P.SDS_EDIT_OTHER_DEPARTMENT)
    ? companyDepartments.value
    : editableDepartments.value;
});

const selectableDepartmentTree = computed(() => {
  return hasPermission(P.SDS_EDIT_OTHER_DEPARTMENT)
    ? departmentTree.value
    : editableDepartmentTree.value;
});

const selectedDepartments = ref<Department[]>([...ownDepartments.value]);

const showPdf = async (file: File, sdsUuid: string): Promise<void> => {
  pdfFile.name = file.name;
  previewedSdsUuid.value = sdsUuid;

  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = function () {
    pdfBlob.value = reader.result as string;
  };

  selectedPdfDisplayOption.value = 'max';
};

const handleCheckedSdsUuids = (sdsUuidList: string[]): void => {
  checkedSdsUuids.value = sdsUuidList;
  isCheckedAllFiles.value = checkedSdsUuids.value.length === files.value.length;
};

const getTargetFileIndex = (sdsUuid: string): number => {
  return fileMetas.value.findIndex((meta) => meta.sds_uuid === sdsUuid);
};

const isCheckedTag = (tag: Tag): boolean =>
  tempSelectedTags.value.some((t) => t.id === tag.id);

const filteredTags = computed(() => {
  const searchedTags = tags.value.filter((tag) =>
    tag.name.toLowerCase().includes(searchTagKeyword.value.toLowerCase())
  );
  return searchTagKeyword.value.length ? searchedTags : tags.value;
});

const updateSelectedFilesTempTags = (tag: Tag, isAdd: boolean): void => {
  isAdd
    ? (tempSelectedTags.value = [...tempSelectedTags.value, tag])
    : (tempSelectedTags.value = tempSelectedTags.value.filter(
        (t) => t.id !== tag.id
      ));
};

const updateTags = (tags: Tag[], sdsUuid: string): void => {
  const targetFileIndex = getTargetFileIndex(sdsUuid);

  if (0 <= targetFileIndex) {
    fileMetas.value[targetFileIndex].tags = tags;
    files.value[targetFileIndex].tags = tags;

    // filesの変更を検知させる
    files.value = [...files.value];
  }
};

const updateSelectedFilesTags = (): void => {
  checkedSdsUuids.value.forEach((sdsUuid) => {
    updateTags(tempSelectedTags.value, sdsUuid);
  });
  isShowFilesTagOperationDialog.value = false;
};

const removeTag = (tagId: number, sdsUuid: string): void => {
  const targetFileIndex = getTargetFileIndex(sdsUuid);

  if (0 <= targetFileIndex) {
    fileMetas.value[targetFileIndex].tags = fileMetas.value[
      targetFileIndex
    ].tags.filter((tag) => tag.id !== tagId);
    files.value[targetFileIndex].tags = files.value[
      targetFileIndex
    ].tags.filter((tag) => tag.id !== tagId);

    // filesの変更を検知させる
    files.value = [...files.value];
  }
};

const removeFile = async (index: number): Promise<void> => {
  const removedSdsUuid = fileMetas.value[index]?.sds_uuid;
  files.value.splice(index, 1);
  fileMetas.value.splice(index, 1);

  // 削除前のファイル構成で計算された結果が残らないようにクリアする
  duplicates.value = [];
  duplicatesChecked.value = false;

  // 最後の行を削除すると子コンポーネントがwatcher発火前に破棄され
  // checkedSdsUuidsの空更新がemitされないため、ここでクリアする
  if (!files.value.length) {
    checkedSdsUuids.value = [];
    isCheckedAllFiles.value = false;
  }

  // 削除したファイルをプレビュー中の場合は閉じる
  if (removedSdsUuid && removedSdsUuid === previewedSdsUuid.value) {
    pdfBlob.value = '';
    selectedPdfDisplayOption.value = 'none';
    previewedSdsUuid.value = '';
  }

  // 削除により重複状態が変わりうるため（特に同一アップロード内の重複）、再チェックする
  if (files.value.length) {
    await checkDuplicates();
  }
};

const updateDepartments = (departmentIds: string[]): void => {
  selectedDepartments.value = departmentIds.map((id) => ({
    uuid: id,
    name: selectableDepartments.value.find((d) => d.uuid === id)?.name || '',
  }));
};

const pdpageScalefApp = computed((): string => {
  return selectedPdfDisplayOption.value == 'max' ? 'page-width' : 'page-fit';
});

const clickForm = (): void => {
  input.value?.click();
};

const replaceFileName = (name: string): string => {
  const extension = name.split('.').pop();
  const fileNameWithoutExtension = name.replace(
    patternExtractFileExtension,
    ''
  );
  const newFileName = `${fileNameWithoutExtension.replace(
    SYMBOL_REGEX,
    '_'
  )}.${extension}`;
  return newFileName;
};

const detectPdfProtection = async (
  fileName: string,
  pdfData: Uint8Array
): Promise<PdfProtection> => {
  // pdf.jsにPDFデータをセット
  const loadingTask = pdfjsLib.getDocument({ data: pdfData });
  const passwordState = { requested: false };
  loadingTask.onPassword = () => {
    passwordState.requested = true;
    loadingTask.destroy();
  };
  try {
    const pdf = await loadingTask.promise;
    // 添付一覧が読めなくても、本文が読める PDF は受け付ける
    const attachments = await pdf.getAttachments().catch((error: unknown) => {
      console.warn(`Failed to list attachments of ${fileName}`, error);
      return null;
    });
    return hasMicrosoftIrmPayload(attachments) ? 'irm' : null;
  } catch (error) {
    if (passwordState.requested) return 'password';
    throw error;
  } finally {
    await loadingTask.destroy();
  }
};

const validatePdfBytes = async (
  fileName: string,
  bytes: Uint8Array
): Promise<string | null> => {
  const headerKind = classifyPdfHeader(bytes);
  if (headerKind === 'ole2') {
    return $t(m.subjectIsRmsProtected, { subject: fileName });
  }
  if (headerKind === 'unknown') {
    return $t(m.subjectIsNotReadablePdf, { subject: fileName });
  }
  const protection = await detectPdfProtection(fileName, bytes);
  if (protection === 'password') {
    return $t(m.subjectIsPasswordProtected, { subject: fileName });
  }
  if (protection === 'irm') {
    return $t(m.subjectIsRmsProtected, { subject: fileName });
  }
  return null;
};

const inspectFile = async (file: File): Promise<InspectedFile> => {
  if (file.size === 0) {
    return {
      hash: null,
      message: $t(m.subjectIsEmpty, { subject: file.name }),
    };
  }

  const buffer = await file.arrayBuffer().catch((error: unknown) => {
    console.error(`Failed to read ${file.name}`, error);
    return null;
  });
  if (buffer === null) {
    return {
      hash: null,
      message: $t(m.subjectReadFailed, { subject: file.name }),
    };
  }

  const hash = await calculateFileHash(buffer, window);
  try {
    const message = await validatePdfBytes(file.name, new Uint8Array(buffer));
    return { hash, message };
  } catch (error) {
    console.error(`Failed to check ${file.name}`, error);
    return {
      hash,
      message: $t(m.subjectIsNotReadablePdf, { subject: file.name }),
    };
  }
};

const showUploadDialog = () => {
  uploadDialog.value = true;
};

const closeUploadDialog = () => {
  uploadDialog.value = false;
};

const reset = (): void => {
  files.value.splice(0);
  fileMetas.value.splice(0);
  pdfBlob.value = '';
  previewedSdsUuid.value = '';
  selectedPdfDisplayOption.value = 'none';
  selectedDepartments.value = [...ownDepartments.value];
  checkedSdsUuids.value = [];
  duplicates.value = [];
  duplicatesChecked.value = false;
  uploaded.value = 0;
  isCheckedAllFiles.value = false;
};

const selectFile = async (e: Event): Promise<void> => {
  const inputFiles =
    e.target instanceof HTMLInputElement ? e.target.files : null;
  const droppedFiles = e instanceof DragEvent ? e.dataTransfer?.files : null;
  const targetFiles = inputFiles?.length ? inputFiles : droppedFiles;
  if (!targetFiles?.length) {
    return;
  }

  clearAlertMessages();

  const sizeValidFiles = Array.from(targetFiles).filter((file) => {
    if (file.size > MAX_FILE_SIZE) {
      showUploadDialog();
      pushAlertMessage(
        $t(m.subjectExceedsFileSizeLimit, { subject: file.name })
      );
      return false;
    }
    return true;
  });

  if (sizeValidFiles.length > 50) return showUploadDialog();
  const res = sizeValidFiles.filter((file) => {
    if (file.name.split('.').pop()?.toLocaleLowerCase() == 'pdf') {
      return true;
    }
    pushAlertMessage($t(m.subjectIsNotPdf, { subject: file.name }));
    return false;
  });

  const accepted: Array<{ file: FileWithTag; hash: string | null }> = [];
  for (const file of res) {
    const { hash, message } = await inspectFile(file);
    if (message) {
      pushAlertMessage(message);
      continue;
    }
    accepted.push({ file: Object.assign(file, { tags: [] }), hash });
  }

  if (accepted.length === 0) {
    return;
  }

  const metas: Array<CreateCompanySdsRequestUnit> = accepted.map(
    ({ file, hash }) => {
      const timestamp = Math.floor(Date.now() / 1000);

      const obj: CreateCompanySdsRequestUnit = {
        sds_uuid: UUID.generate(),
        filename: file.name,
        replaced_name: replaceFileName(file.name),
        timestamp: timestamp,
        upload_filename: `${timestamp}.${file.name}`,
        gcs_signed_url: '',
        note: '',
        tags: [],
        hash_value: hash,
      };
      return obj;
    }
  );
  files.value.splice(0);
  files.value.push(...accepted.map(({ file }) => file));
  fileMetas.value.splice(0);
  fileMetas.value.push(...metas);
  duplicates.value = [];
  duplicatesChecked.value = false;

  await checkDuplicates();
};

// チェック中のキャンセル→再選択で、古いチェックの結果が新しいファイル構成に
// 混ざらないように実行世代を管理する
const checkDuplicatesGeneration = ref(0);

const checkDuplicates = async (): Promise<void> => {
  const generation = ++checkDuplicatesGeneration.value;
  const metasSnapshot = [...fileMetas.value];
  isCheckingDuplicates.value = true;
  try {
    const res = await dispatchCheckCompanySdsesDuplicates(store, {
      companyId: readAssertedCompanyId(store),
      sdsList: metasSnapshot.map((meta) => ({
        sds_uuid: meta.sds_uuid,
        filename: meta.filename,
        hash_value: meta.hash_value,
      })),
    });
    if (generation !== checkDuplicatesGeneration.value) return;
    duplicates.value = res.data.duplicates;
    duplicatesChecked.value = true;
  } catch (e) {
    console.error(e);
  } finally {
    // 古い実行はフラグに触らない（最新の実行が完了時に解除する）
    if (generation === checkDuplicatesGeneration.value) {
      isCheckingDuplicates.value = false;
    }
  }
};

const createFiles = async () => {
  // Re-entry guard: prevent a second click on 登録 (or any programmatic
  // re-invocation) from starting a parallel upload while one is already
  // running.
  if (isUploading.value) {
    return;
  }
  if (!files.value.length) {
    return;
  }
  isUploading.value = true;
  uploaded.value = 0;
  try {
    // Snapshot files, fileMetas, and selected departments at the start of
    // the upload. Without these snapshots, a user who clicks Cancel and
    // re-drops the same file while the GCS PUT is in flight can cause
    // `fileMetas.value` to be replaced (selectFile does splice(0) + push),
    // and the subsequent `dispatchCreateCompanySdses` call would send the
    // newly-generated timestamp — leaving the GCS object at the original
    // prefix while `sds.timestamp` in the DB is written with a later value.
    // See issue #2282.
    const filesSnapshot = [...files.value];
    const metasSnapshot: CreateCompanySdsRequestUnit[] = fileMetas.value.map(
      (m) => ({ ...m })
    );
    const departmentIdsSnapshot = selectedDepartments.value.map((d) => d.uuid);

    const res = await dispatchGetGcsSignedUrl(store, {
      companyId: readAssertedCompanyId(store),
      sdsList: metasSnapshot,
    });

    const uploadFile = async (
      file: File,
      metadata: CreateCompanySdsRequestUnit
    ) => {
      const { gcs_signed_url: url, replaced_name_encoded: encodedName } =
        metadata;
      if (!url || !encodedName) {
        throw new Error(`Missing GCS upload metadata for ${file.name}`);
      }
      const response = await fetch(url, {
        headers: {
          'x-goog-meta-sds_uuid': metadata.sds_uuid,
          'x-goog-meta-timestamp': String(metadata.timestamp),
          'x-goog-meta-company_id': readAssertedCompanyId(store),
          'x-goog-meta-replaced_name_encoded': encodedName,
        },
        method: 'PUT',
        body: file,
      });
      if (!response.ok) {
        throw new Error(
          `GCS upload failed: ${response.status} ${response.statusText}`
        );
      }
      uploaded.value++;
    };

    const gcsPromises = filesSnapshot.map((file, index) => {
      const metadata = res.data[index];
      return uploadFile(file, metadata);
    });

    const gcsResults = await Promise.allSettled(gcsPromises);
    const gcsFailure = gcsResults.find(
      (result) => result.status === 'rejected'
    );
    if (gcsFailure?.status === 'rejected') {
      throw gcsFailure.reason;
    }

    await dispatchCreateCompanySdses(store, {
      companyId: readAssertedCompanyId(store),
      departmentIdList: departmentIdsSnapshot,
      sdsList: metasSnapshot,
    });

    commitAddNotification(store, {
      messageKey: m.uploadAccepted,
      color: 'success',
    });

    updateSdsCount();
  } catch (e) {
    console.error(e);
    // API errors are already handled by dispatchCheckApiError in the store actions.
    // Only show uploadFailed for non-API errors (e.g. GCS upload failures via fetch).
    if (!(e as { response?: unknown })?.response) {
      commitAddNotification(store, {
        messageKey: m.uploadFailed,
        color: 'error',
      });
    }
  } finally {
    isUploading.value = false;
  }
};

const fetchCompanySdsesCount = async (): Promise<void> => {
  const res = await dispatchGetCompanySdsesCount(store, {
    companyId: readAssertedCompanyId(store),
    data: {},
  });
  if (res) {
    commitSetCompany(store, {
      ...readAssertedCompany(store),
      uploaded_file_number: res.data.total,
    });
  }
};

watch(isShowFilesTagOperationDialog, () => {
  if (!isShowFilesTagOperationDialog.value) {
    tempSelectedTags.value = [];
  }
});

onMounted((): void => {
  input.value;
  getCompanyDepartments();
  getCompanyDepartmentTree();
  fetchCompanySdsesCount();
  getCompanyTag();
});

return { __sfc: true,tags, getCompanyTag, $t, pdfDisplayOptions, m, companyDepartments, getCompanyDepartments, departmentTree, getCompanyDepartmentTree, ownDepartments, editableDepartments, editableDepartmentTree, hasPermission, P, helpPageUrl, selectedPdfDisplayOption, pdfBlob, previewedSdsUuid, files, fileMetas, sdsUuids, jsonInput, isJsonExporting, uploadWithJson, selectJsonFile, input, isUploading, uploaded, isCheckedAllFiles, checkedSdsUuids, isShowFilesTagOperationDialog, searchTagKeyword, tempSelectedTags, pdfFile, uploadDialog, duplicates, duplicatesChecked, isCheckingDuplicates, alertMessages, clearAlertMessages, pushAlertMessage, isValidForm, selectableDepartments, selectableDepartmentTree, selectedDepartments, showPdf, handleCheckedSdsUuids, getTargetFileIndex, isCheckedTag, filteredTags, updateSelectedFilesTempTags, updateTags, updateSelectedFilesTags, removeTag, removeFile, updateDepartments, pdpageScalefApp, clickForm, replaceFileName, detectPdfProtection, validatePdfBytes, inspectFile, showUploadDialog, closeUploadDialog, reset, selectFile, checkDuplicatesGeneration, checkDuplicates, createFiles, fetchCompanySdsesCount, BaseButtonOutlined, BaseButtonSubmit, PdfViewer, UploadFileTable, DepartmentTreeSelect, BaseBottomSheet, TagChip, sharedMessages }
}

})